import React, { CSSProperties, Fragment, JSXElementConstructor } from 'react';
import connect from '@src/js/separate-logic/connect';
import {
    AttachmentInfo,
    AttachmentProps,
    AttachmentToolbarProps,
} from '@src/slide/domain/ui-model/element-modeles/property/attachment/interface';
import AttachmentPresenter from '@editor/src/page/index/components/canvas/attachment/attachment-presenter';
import AttachmentModel from '@editor/src/page/index/components/canvas/attachment/attachment-model';
import type { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import type { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { DEFAULT_PADDING_LR, DEFAULT_PADDING_TB, FONT_SCALE } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import {
    H5_PLAY_EVENT,
    TEXT_LINK_DEFAULT_FONT_SIZE,
    TEXT_LINK_EFFECT_FONT_FAMILY,
} from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import { isVideoElement } from '@src/slide/domain/ui-model/elements/element-judge';
import {
    hideMqqNavigation,
    keepSafeAreaInTencentDocApp,
    showMqqNavigation,
} from '@src/slide/infrastructure/module/independent/mobile/mqq-navigation';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { createGraphicsParser } from '@tencent/alloy-graphics';
import { IPath } from '@tencent/alloy-graphics/lib/types/path';


interface IAttachmentProps {
    attachment: AttachmentInfo;
    AttachmentComponent: JSXElementConstructor<AttachmentProps>;
    AttachmentToolbar: JSXElementConstructor<AttachmentToolbarProps>;
    containerID: string;
    clipPathId: string;
    setRef: (name: string, ref: any) => void;
    audioAndVideoParams?: Record<string, any>
    uiModel: IShapeModel | IPictureModel;
    isPlayMode?: boolean;
    directShowVideoPlayer?: boolean;
    isDisableDownload?: boolean;
    isDisableVideoResolution?: boolean;
    isUploading: (id?: string) => boolean;
    bgShapeOptions?: any;
    isDisableClip: boolean;
    setVideoCanPlay: (canPlay: boolean) => void;
    isVideoFullScreen: boolean;
    setIsVideoFullScreen: (isVideoFullScreen: boolean) => void;
    getLocalAttachment: () => AttachmentInfo;
}

export class Attachment extends React.Component<IAttachmentProps> {
    public static displayName = 'Attachment'; // 声明displayName，单元测试用于查找组件

    public static defaultProps = {
        setRef: () => {},
    };

    videoPlayerInstance: any;
    videoResizeObserver: ResizeObserver;
    shouldStopSetObjectFit: boolean;
    attachmentContainerRef: HTMLDivElement | null;

    // eslint-disable-next-line complexity
    public render() {
        const {
            AttachmentComponent,
            containerID,
            setRef,
            audioAndVideoParams,
            isPlayMode = false,
            directShowVideoPlayer = true,
            uiModel,
            isDisableDownload = false,
            isDisableVideoResolution = false,
            isVideoFullScreen,
            getLocalAttachment,
        } = this.props;

        const attachment = getLocalAttachment?.() || this.props.attachment;

        // 演示态不显示正在上传中的附件，否则 uploader.showAttachment 会把当前上传中的附件展示到演示态中，编辑区正在上传的附件就不显示了
        if (isPlayMode && this.props.isUploading()) {
            // 视频实现了边传边播，即使正在上传也能展示
            if (!isVideoElement(uiModel)) {
                return null;
            }
        }

        const commonStyle: CSSProperties = isPlayMode ? {
            // 演示态下需要可以点击附件
            pointerEvents: 'auto',
        } : {};

        const textLinkStyle: CSSProperties = {
            ...commonStyle,
            position: 'absolute',
            left: (DEFAULT_PADDING_LR * FONT_SCALE),
            top: (DEFAULT_PADDING_TB * FONT_SCALE),
            lineHeight: 1.3,
            transform: `scale(${FONT_SCALE})`,
            transformOrigin: 'left top',
            wordBreak: 'keep-all',
            whiteSpace: 'nowrap',
        };

        const previewStyle: CSSProperties = {
            ...commonStyle,
            position: 'absolute',
            transform: `scale(${FONT_SCALE})`,
            transformOrigin: 'left top',
            left: 0,
            top: 0,
            width: `${(1 / FONT_SCALE) * 100}%`,
            height: `${(1 / FONT_SCALE) * 100}%`,
        };

        const attachmentInfo = {
            ...attachment,
            styles: {
                ...attachment?.styles ?? {},
                ...{
                    '.text-link': {
                        textDecoration: 'none',
                        // 字体和大小和 canvas 文字链保持一致，保证选中框的尺寸和附件文字链尺寸一致
                        fontFamily: TEXT_LINK_EFFECT_FONT_FAMILY.join(','),
                        fontSize: `${TEXT_LINK_DEFAULT_FONT_SIZE}px`,
                    },
                    '.attachment-wrapper': { padding: 0 },
                    '.attachment-picture-wrapper': { borderRadius: 0 },
                    '.attachment-video-wrapper': { backgroundSize: '100% 100%', background: 'transparent' },
                },
            },
        };
        const videoPlacerHolderPic = isVideoElement(uiModel) ? (uiModel as IPictureModel)?.props?.src : undefined;
        // 本地编辑场景 padId 为空，使用 localPadId
        const { padId = '', localPadId = '' } = SlideDataServices.DocInfoManager() || {};
        // video 需要被裁剪样式
        isVideoElement(uiModel) && (
            isVideoFullScreen
                ? this.clearVideoStyle(this.videoPlayerInstance)
                : this.setVideoClipStyle(this.videoPlayerInstance)
        );
        return (
            <Fragment>
                {
                    isVideoElement(uiModel) && <svg width="0" height="0">
                        <defs>
                            <clipPath id={this.getCurClipPathId()}>
                                {
                                    this.getClipPath()?.map(({ svgPath }) => (
                                        <path d={svgPath} key={svgPath}></path>))
                                }
                            </clipPath>
                        </defs>
                    </svg>
                }
                <div
                    id={containerID}
                    style={
                        (attachment.showType === 'preview' || attachment.showType === 'card') ? { ...previewStyle } : { ...textLinkStyle }
                    }
                    ref={(ref) => {
                        this.attachmentContainerRef = ref;
                        this.setVideoClipStyle();
                        setRef('attachmentContainer', ref);
                    }}
                    onClick={this.stopPropagationInPlayer}
                    onTouchEnd={this.stopPropagationInPlayer}
                    onMouseDown={browser.isPad ? this.stopPropagationInPlayer : undefined}
                >
                    {AttachmentComponent && (
                        <AttachmentComponent
                            attachment={attachmentInfo}
                            isCircleAudio
                            /**
                         * 这里可能设置为 true 的原因：
                         * slide 的视频默认显示的是 canvas 的封面图，点击之后要切换为附件 dom 并播放
                         * 如果这里为 false 的话，切换之后默认展示的是附件组件的封面图，只有再次点击才会播放
                         * 如果为 true 的话，只要渲染了这个组件，就会直接转码并把视频组件渲染出来
                         */
                            directShowVideoPlayer={directShowVideoPlayer}
                            videoPlacerHolderPic={videoPlacerHolderPic}
                            padId={padId || localPadId}
                            isDisableDownload={isDisableDownload}
                            isDisableVideoResolution={isDisableVideoResolution}
                            audioAndVideoParams={{
                                ...audioAndVideoParams,
                                playerInstanceRef: this.setPlayerInstanceRef,
                                onFullScreenChange: (isVideoFullScreen: boolean) => {
                                    audioAndVideoParams?.onFullScreenChange?.(isVideoFullScreen);
                                    this.handleVideoFullScreen(isVideoFullScreen);
                                },
                            }}
                            onFullScreenChange={this.handleFullScreenChange}
                        />
                    )}
                </div>
            </Fragment>
        );
    }

    /**
     * 演示态中阻止冒泡，避免触发翻页逻辑
     * @param e
     */
    private stopPropagationInPlayer = (e: React.MouseEvent | React.TouchEvent) => {
        if (this.props.isPlayMode) {
            e.stopPropagation();
        }
    };

    /**
     * 全屏预览时隐藏和显示移动端导航条，否则会遮住全屏预览的关闭按钮
     * @param isFullScreen
     */
    private handleFullScreenChange = (isFullScreen: boolean) => {
        if (this.props.isPlayMode) {
            if (isFullScreen) {
                keepSafeAreaInTencentDocApp();
            }
            return;
        }
        if (isFullScreen) {
            hideMqqNavigation();
            keepSafeAreaInTencentDocApp();
        } else {
            showMqqNavigation();
            SlideDataServices.SlideTitleManager()?.updateTitle();
        }
    };

    private handleVideoFullScreen(isVideoFullScreen: boolean) {
        this.props?.setIsVideoFullScreen?.(isVideoFullScreen);
        if (isVideoFullScreen) {
            this.clearVideoStyle(this.videoPlayerInstance);
        }
    }

    // 获取 super player 实例 (video attachment 才会有)
    private setPlayerInstanceRef = (player) => {
        const { playerInstanceRef } = this.props.audioAndVideoParams || {};
        if (playerInstanceRef) {
            // PlayerInstance 加载完成后，对其 video 节点进行裁剪和设置样式
            player.on(H5_PLAY_EVENT.VIDEO_CANPLAY, () => {
                this.videoPlayerInstance = player;
                this.props.isVideoFullScreen ? this.clearVideoStyle(player) : this.setVideoClipStyle(player);
            });

            // todo [super player bug]: 即使按文档修改了元素的 css 属性 object-fit 为 fill，
            //  其内部依然会强制将元素 object-fill 设置为 contain
            player.on(H5_PLAY_EVENT.VIDEO_LOAD_START, () => {
                player.videoElements.forEach((targetNode: HTMLVideoElement) => {
                    this.shouldStopSetObjectFit = false;
                    this.keepSettingObjectFit(targetNode, this.props.isVideoFullScreen  ? 'contain' : 'fill');
                });
            });
            player.on(H5_PLAY_EVENT.VIDEO_LOAD_PROGRESS, () => {
                this.shouldStopSetObjectFit = true;
            });
            player.on(H5_PLAY_EVENT.VIDEO_PLAYING, () => {
                player.videoElements.forEach((targetNode: HTMLVideoElement) => {
                    targetNode.style['object-fit'] = this.props.isVideoFullScreen ?  'contain' : 'fill';
                });
            });

            playerInstanceRef(player);
        }
    };

    private keepSettingObjectFit = (el, objectFit: string) => {
        el.style.objectFit = objectFit;
        if (!this.shouldStopSetObjectFit) {
            requestAnimationFrame(this.keepSettingObjectFit.bind(this, el));
        }
    };

    /**
     * 获取裁剪用到的 path
     * @returns
     */
    private getClipPath(): IPath[] | null {
        if (this.props.isDisableClip) return null;

        const { uiModel, bgShapeOptions } = this.props;
        // 创建 svg clip路径
        const shape = createGraphicsParser({
            type: uiModel?.getGraphic()?.getGraphicInfo()?.bgShape,
            controlValues: uiModel?.getGraphic()?.getGraphicInfo()?.controlValues,
            width: bgShapeOptions?.width / 2,
            height: bgShapeOptions?.height / 2,
        });
        return shape.svgPathInRect();
    }

    private getCurClipPathId() {
        const { clipPathId, isPlayMode, uiModel } = this.props;
        return `${isPlayMode}_${clipPathId}_${uiModel.key}`;
    }

    /**
     * 设置视频裁剪样式
     * @param player instance
     */
    private setVideoClipStyle(player?: any) {
        const { uiModel, isDisableClip, isVideoFullScreen } = this.props;
        if (isDisableClip || !isVideoElement(uiModel)) return;
        if (player) {
            const { width, height, srcRect } = uiModel.props.style;
            const { pl, pt, rw, rh } = uiModel.getImageSize().getForegroundImageSize({ width, height, srcRect });
            const objectFit = isVideoFullScreen ? 'contain' : 'fill';
            player.videoElements.forEach((ele: HTMLVideoElement) => {
                ele.style.transform = `translate(${-(pl / 2)}px, ${-(pt / 2)}px)`;
                ele.style.width = `${rw / 2}px`;
                ele.style.height = `${rh / 2}px`;
                ele.style['object-fit'] = objectFit; // GOTO todo [super player bug]:
            });
            // todo [super player bug]: 即使按文档修改了元素的 css 属性 object-fit 为 fill，
            //  其内部依然会强制将元素 object-fill 设置为 contain，待 bug 修复后可注释掉
            player.videoElements.forEach((targetNode: HTMLVideoElement) => {
                this.videoResizeObserver?.disconnect();
                this.videoResizeObserver = new ResizeObserver(() => {
                    targetNode.style['object-fit'] = objectFit;
                });
                this.videoResizeObserver.observe(targetNode);
            });
            // 设置 video 裁剪
            player.videoElementsContainer.style.clipPath = `url(#${this.getCurClipPathId()})`;
        } else if (this.attachmentContainerRef) {
            // 预览态时，dc-video 组件无 playerIns，并且使用组件内部的封面图（attachment-video-placeholder-cover）,需要对其进行裁剪
            const videoPlaceHolderCoverContainer = this.attachmentContainerRef.querySelector('.attachment-video-placeholder-container');
            if (videoPlaceHolderCoverContainer) {
                (videoPlaceHolderCoverContainer as HTMLDivElement).style.clipPath = `url(#${this.getCurClipPathId()})`;
            }
            const videoPlaceHolderCover = this.attachmentContainerRef.querySelector('.attachment-video-placeholder-cover');
            if (videoPlaceHolderCover) {
                (videoPlaceHolderCover as HTMLDivElement).style.backgroundSize = '100% 100%';
                // 裁剪后 playerIcon 会被覆盖
                const videoPlaceHolderIcon = this.attachmentContainerRef.querySelector('.attachment-video-placeholder-play-icon');
                videoPlaceHolderIcon && ((videoPlaceHolderIcon as HTMLDivElement).style.zIndex = '2');
            }
        }
    }

    private clearVideoStyle(player) {
        if (!player) return;
        player.videoElementsContainer.style.clipPath = '';
        player.videoElements.forEach((ele: HTMLVideoElement) => {
            ele.style.transform = '';
            ele.style.width = '100%';
            ele.style.height = '100%';
            ele.style['object-fit'] = 'contain';
        });
    }
}

export default connect(
    AttachmentPresenter,
    AttachmentModel,
    Attachment,
);
